import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./redux";
import { authSelector, isAdminSelector } from "../store/slices/authSlice";
import { userIdSelector } from "../store/slices/authSlice";
import {
  currentUserIdSelector,
  userProfileSelector,
  userLoadingSelector,
  vendorProfileSelector,
} from "../store/slices/userSlice";
import { itemSelector } from "../store/slices/itemSlice";
import { locationSelector } from "../store/slices/locationSlice";
import { categoriesSelector } from "../store/slices/categorySlice";
import {
  conditionsSelector,
  reviewCategoriesSelector,
} from "../store/slices/conditionSlice";
import { getSearchFilterConditionOptions } from "../utils/searchItemCondition";

const useCommonData = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userToken = useAppSelector(authSelector);
  const userId = useAppSelector(userIdSelector);
  const currentUserId = useAppSelector(currentUserIdSelector);
  const userProfile = useAppSelector(userProfileSelector);
  const vendorProfile = useAppSelector(vendorProfileSelector);
  const itemState = useAppSelector(itemSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const locationState = useAppSelector(locationSelector);
  const categoriesState = useAppSelector(categoriesSelector);
  const adminState = useAppSelector((state) => state.admin);
  const isSuperAdmin = useAppSelector((state) => state.auth.isSuperAdmin);
  const userLoading = useAppSelector(userLoadingSelector);
  const isBlockedByAdmin = Boolean(userProfile?.blockedByAdmin);
  const categoryOptions = useMemo(() =>
    categoriesState.map(category => ({
      label: category.categoryName,
      value: category.categoryId,
    })),
  [categoriesState]);
  const itemConditionsState = useAppSelector(conditionsSelector);
  const itemConditionsOptions = useMemo(() =>
    itemConditionsState.map(condition => ({
      label: condition.name,
      value: condition.id,
    })),
  [itemConditionsState]);
  const searchConditionFilterOptions = useMemo(
    () => getSearchFilterConditionOptions(itemConditionsState),
    [itemConditionsState]
  );
  const itemReviewCategoriesState = useAppSelector(reviewCategoriesSelector);
  const itemReviewCategoriesOptions = useMemo(() =>
    itemReviewCategoriesState.map(category => ({
      label: category.name,
      value: category.id,
    })),
  [itemReviewCategoriesState]);
  const isAdminPanel = useMemo(() => {
    return isAdmin && location.pathname.startsWith("/admin");
  }, [isAdmin, location.pathname]);
  const showAppDashboard = useMemo(() => {
    return isAdmin && !location.pathname.startsWith("/admin");
  }, [isAdmin, location.pathname]);

  return {
    dispatch,
    navigate,
    location,
    userToken,
    userId,
    isAdmin,
    currentUserId,
    userProfile,
    vendorProfile,
    userLoading,
    isBlockedByAdmin,
    itemState,
    locationState,
    categoriesState,
    adminState,
    isSuperAdmin,
    itemConditionsState,
    itemConditionsOptions,
    searchConditionFilterOptions,
    itemReviewCategoriesState,
    itemReviewCategoriesOptions,
    categoryOptions,
    isAdminPanel,
    showAppDashboard,
  };
};

export default useCommonData;
